import SelectComponent from "@via/components/select/select";
import { SelectOption, isSelectOption } from "@via/utils/interfaces";
import { GCPVMInstanceType, WorkbenchOwnerSummary } from "common";
import { useEffect, useMemo, useState } from "react";
import { classNames } from "@via/utils/helpers.tsx";

export type StatusFilterValue = "running" | "stopped" | "terminated";

interface WorkbenchFilterBarProps {
  owners: WorkbenchOwnerSummary[];
  selectedInstanceType: GCPVMInstanceType | null;
  selectedOwner: string | null;
  selectedStatuses: StatusFilterValue[];
  searchTerm: string;
  onInstanceTypeChange: (instanceType: GCPVMInstanceType | null) => void;
  onOwnerChange: (owner: string | null) => void;
  onStatusChange: (statuses: StatusFilterValue[]) => void;
  onSearchChange: (value: string) => void;
  onClearAll: () => void;
}

export default function WorkbenchFilterBar({
  owners,
  selectedInstanceType,
  selectedOwner,
  selectedStatuses,
  searchTerm,
  onInstanceTypeChange,
  onOwnerChange,
  onStatusChange,
  onSearchChange,
  onClearAll,
}: WorkbenchFilterBarProps) {
  // Get all instance types from GCPVMInstanceType enum
  const instanceTypeOptions = useMemo(() => {
    return Object.values(GCPVMInstanceType)
      .sort()
      .map(type => ({
        label: type,
        value: type,
      })) as SelectOption[];
  }, []);

  // Extract unique owners
  const ownerOptions = useMemo(() => {
    const uniqueOwners = new Map<string, string>();
    owners.forEach(owner => {
      if (owner.username) {
        uniqueOwners.set(owner.username, owner.username);
      }
    });
    return Array.from(uniqueOwners.keys())
      .sort()
      .map(owner => ({
        label: owner,
        value: owner,
      })) as SelectOption[];
  }, [owners]);

  // Status options
  const statusOptions: SelectOption[] = [
    { label: "Running", value: "running" },
    { label: "Paused", value: "stopped" },
    { label: "Terminated", value: "terminated" },
  ];

  const [searchValue, setSearchValue] = useState(searchTerm);

  useEffect(() => {
    setSearchValue(searchTerm);
  }, [searchTerm]);

  const hasActiveFilters =
    selectedInstanceType !== null ||
    selectedOwner !== null ||
    selectedStatuses.length > 0 ||
    searchTerm.trim().length > 0;

  return (
    <div className="w-full my-4 flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-end">
      <div className="flex-1 min-w-0">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Search
        </label>
        <input
          type="text"
          value={searchValue}
          onChange={event => {
            setSearchValue(event.target.value);
          }}
          onKeyDown={event => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSearchChange(searchValue.trim());
            }
          }}
          placeholder="Search by Workbench or Name"
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>
      {/* Instance Type Filter */}
      <div className="flex-1 min-w-0">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Instance Type
        </label>
        <SelectComponent
          name="instanceTypeFilter"
          options={instanceTypeOptions}
          value={
            selectedInstanceType
              ? instanceTypeOptions.find(
                  opt => opt.value === selectedInstanceType
                ) || null
              : null
          }
          onChange={selected => {
            if (isSelectOption(selected)) {
              onInstanceTypeChange(selected.value as GCPVMInstanceType);
              return;
            }
            onInstanceTypeChange(null);
          }}
          placeholder="All Types"
          isSearchable={true}
        />
      </div>

      {/* Owner Filter */}
      <div className="flex-1 min-w-0">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Owner
        </label>
        <SelectComponent
          name="ownerFilter"
          options={ownerOptions}
          value={
            selectedOwner
              ? ownerOptions.find(opt => opt.value === selectedOwner) || null
              : null
          }
          onChange={selected => {
            if (isSelectOption(selected)) {
              onOwnerChange(selected.value as string);
              return;
            }
            onOwnerChange(null);
          }}
          placeholder="All Owners"
          isSearchable={true}
        />
      </div>

      {/* Status Filter (Multi-select) */}
      <div className="flex-1 min-w-0">
        <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
          Status
        </label>
        <SelectComponent
          name="statusFilter"
          options={statusOptions}
          isMulti={true}
          value={statusOptions.filter(opt =>
            selectedStatuses.includes(opt.value as StatusFilterValue)
          )}
          onChange={selected => {
            if (Array.isArray(selected)) {
              onStatusChange(
                selected.map(opt => opt.value as StatusFilterValue)
              );
            } else {
              onStatusChange([]);
            }
          }}
          placeholder="All Statuses"
          isSearchable={false}
        />
      </div>

      {/* Clear All Button */}
      <div className="flex-shrink-0">
        <button
          type="button"
          onClick={onClearAll}
          disabled={!hasActiveFilters}
          className={classNames(
            "rounded-md px-4 py-2 text-sm font-semibold",
            "transition-colors duration-150",
            hasActiveFilters
              ? "bg-gray-100 text-gray-900 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              : "bg-gray-50 text-gray-400 cursor-not-allowed"
          )}>
          Clear All
        </button>
      </div>
    </div>
  );
}
